<template>
    <Popover v-model:open="isOpen">
        <PopoverTrigger as-child>
            <button
                type="button"
                class="h-full flex items-center gap-x-2 px-3 bg-white border rounded-md text-left text-base font-semibold text-dark-gray-600 whitespace-nowrap"
                :class="isOpen ? 'border-black' : 'border-gray-200'"
            >
                <span>{{ triggerLabel }}</span>
                <ChevronDown class="size-5 transform transition-transform" :class="{ 'rotate-180': isOpen }" aria-hidden="true" />
            </button>
        </PopoverTrigger>
        <PopoverContent align="end" :collision-padding="16" class="w-auto min-w-[200px] max-w-[calc(100vw-2rem)]">
            <!-- ref'd wrapper, not PopoverContent itself: the card's own w-auto sizes to this
                 div's content, so animating this div's width (see animatePopoverWidth) drags the
                 card along with it. No overflow-hidden here (on this div or PopoverContent) - the
                 Custom view's calendar is deliberately wider than the card and expected to hang
                 off both sides as its own floating panel; clipping the card would clip it too. -->
            <div ref="popoverBodyEl" class="transition-[width] duration-200 ease-out">
                <div class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400 mb-2">{{ $t('global.uiElements.periodRangeDropdown.sectionLabel') }}</div>
                <RadioGroup :model-value="draftChoice" class="flex flex-col gap-y-1" @update:model-value="onChoiceSelected">
                    <div
                        v-for="option in presetOptions"
                        :key="option.months"
                        class="flex items-center gap-x-2 rounded-md px-2 py-1.5 -mx-2"
                        :class="{ 'bg-primary-300 bg-opacity-20': draftChoice === `preset-${option.months}` }"
                    >
                        <RadioGroupItem :id="`period-range-dropdown-preset-${option.months}`" :value="`preset-${option.months}`" />
                        <Label :for="`period-range-dropdown-preset-${option.months}`" class="font-normal cursor-pointer w-full">{{ option.label }}</Label>
                    </div>
                    <Separator class="my-1" />
                    <div class="flex items-center gap-x-2 rounded-md px-2 py-1.5 -mx-2" :class="{ 'bg-primary-300 bg-opacity-20': draftChoice === 'custom' }">
                        <RadioGroupItem id="period-range-dropdown-custom" value="custom" />
                        <Label for="period-range-dropdown-custom" class="font-normal cursor-pointer w-full">{{ $t('global.uiElements.periodRangeDropdown.customOptionLabel') }}</Label>
                    </div>
                </RadioGroup>

                <!-- show-overlay off: its dimming bar sits at a higher z-index than the calendar itself,
                     which here (a small popover, not a full-screen field) rendered as a shadow across the
                     top of the calendar. The popover's own backdrop/click-away already covers that need. -->
                <div v-if="draftChoice === 'custom'" class="flex flex-col gap-y-2 mt-2">
                    <div ref="fromFieldEl" class="period-range-dropdown-field">
                        <DatePicker
                            :label="$t('global.uiElements.periodRangeDropdown.fromFieldLabel')"
                            required
                            :resetable="false"
                            :value="draftFrom"
                            :max="draftTo"
                            :position="fromCalendarPosition"
                            :show-overlay="false"
                            @update:value="(value) => onFieldChange('from', value)"
                        />
                    </div>
                    <div ref="toFieldEl" class="period-range-dropdown-field">
                        <DatePicker
                            :label="$t('global.uiElements.periodRangeDropdown.toFieldLabel')"
                            required
                            :resetable="false"
                            :value="draftTo"
                            :min="draftFrom"
                            :position="toCalendarPosition"
                            :show-overlay="false"
                            @update:value="(value) => onFieldChange('to', value)"
                        />
                    </div>
                </div>

                <div v-if="draftChoice === 'custom'" class="flex justify-end gap-x-2 mt-4 pt-3 border-t">
                    <Button variant="ghost" size="sm" @click="onCancel">{{ $t('global.uiElements.periodRangeDropdown.cancelButtonLabel') }}</Button>
                    <Button size="sm" @click="onApply">{{ $t('global.uiElements.periodRangeDropdown.applyButtonLabel') }}</Button>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { ChevronDown } from 'lucide-vue-next';
import { subMonths, parseISO, startOfDay } from 'date-fns';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import DatePicker from '@/components/ui/DatePicker.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { splitPeriodRangeString, joinPeriodRangeString } from '@/util/period';

interface Props {
    presetMonths?: number[];
}

const props = withDefaults(defineProps<Props>(), {
    presetMonths: () => [3, 6, 9, 12],
});

const modelValue = defineModel<string>('value', { required: true });

const isOpen = ref(false);
const draftChoice = ref('custom');
const draftFrom = ref('');
const draftTo = ref('');

// Flatpickr's own viewport auto-flip is off (the house `static: true` wrapper convention hands
// positioning to CSS instead), so a field near the bottom of the viewport needs an explicit flip
// to 'above' or its calendar renders past the window edge.
const CALENDAR_HEIGHT_ESTIMATE = 360;

// The calendar's own grid (week numbers + days) is a fixed ~408px, wider than the now
// content-sized popover - centering it under the field (below) can push it past the window edge
// when the popover sits near a viewport edge, so that default needs clamping back on-screen.
const CALENDAR_WIDTH_ESTIMATE = 408;

const popoverBodyEl = ref<HTMLElement | null>(null);
const fromFieldEl = ref<HTMLElement | null>(null);
const toFieldEl = ref<HTMLElement | null>(null);

// FLIP: the card's width is content-driven (shrink-to-fit, see the class on PopoverContent above),
// so there's no fixed start/end value for CSS to transition between. Measure the width before the
// preset/Custom content swaps, let it swap, measure the new natural width, then animate explicitly
// between those two px values before releasing back to auto. The token guards against two toggles
// fired in quick succession: a stale transitionend must not clear a width a newer call just set.
let widthAnimationToken = 0;

async function animatePopoverWidth() {
    const el = popoverBodyEl.value;

    if (!el) {
        return;
    }

    const token = ++widthAnimationToken;
    const fromWidth = el.getBoundingClientRect().width;

    await nextTick();

    if (token !== widthAnimationToken) {
        return;
    }

    el.style.width = 'auto';
    const toWidth = el.getBoundingClientRect().width;

    if (Math.abs(toWidth - fromWidth) < 1) {
        el.style.width = '';
        return;
    }

    el.style.width = `${fromWidth}px`;
    void el.offsetWidth; // force reflow so the line below starts a transition instead of a jump
    el.style.width = `${toWidth}px`;

    await new Promise<void>((resolve) => el.addEventListener('transitionend', () => resolve(), { once: true }));

    if (token === widthAnimationToken) {
        el.style.width = '';
    }
}
const fromCalendarPosition = ref<'below' | 'above'>('below');
const toCalendarPosition = ref<'below' | 'above'>('below');

function positionForField(el: HTMLElement | null): 'below' | 'above' {
    if (!el) {
        return 'below';
    }

    const spaceBelow = window.innerHeight - el.getBoundingClientRect().bottom;

    return spaceBelow < CALENDAR_HEIGHT_ESTIMATE ? 'above' : 'below';
}

function alignCalendarHorizontally(fieldEl: HTMLElement | null) {
    const wrapper = fieldEl?.querySelector<HTMLElement>('.flatpickr-wrapper');

    if (!fieldEl || !wrapper) {
        return;
    }

    const fieldRect = fieldEl.getBoundingClientRect();
    const centeredLeft = fieldRect.left + fieldRect.width / 2 - CALENDAR_WIDTH_ESTIMATE / 2;
    const clampedLeft = Math.min(Math.max(centeredLeft, 8), window.innerWidth - CALENDAR_WIDTH_ESTIMATE - 8);

    wrapper.style.left = `${clampedLeft - fieldRect.left}px`;
    wrapper.style.transform = 'none';
}

async function updateCalendarPositions() {
    // The popover's own content mounts (and settles its floating-ui placement) a frame after the
    // `open`/`draftChoice` state that reveals it, so a plain `nextTick` alone can still measure a
    // pre-layout position. Waiting a paint past it gives the final, on-screen geometry.
    await nextTick();
    await new Promise(requestAnimationFrame);

    fromCalendarPosition.value = positionForField(fromFieldEl.value);
    toCalendarPosition.value = positionForField(toFieldEl.value);
    alignCalendarHorizontally(fromFieldEl.value);
    alignCalendarHorizontally(toFieldEl.value);
}

function monthsLabel(months: number) {
    return i18n.global.t('global.uiElements.periodRangeDropdown.monthsPresetLabel').replace('$NUMBER', `${months}`);
}

const presetOptions = computed(() => props.presetMonths.map((months) => ({ months, label: monthsLabel(months) })));

function matchingPresetMonths(from: string, to: string): number | null {
    const toDate = parseISO(to);
    const fromDate = startOfDay(parseISO(from)).getTime();

    return props.presetMonths.find((months) => startOfDay(subMonths(toDate, months)).getTime() === fromDate) ?? null;
}

function seedDraft() {
    const [from, to] = splitPeriodRangeString(modelValue.value);
    const preset = matchingPresetMonths(from, to);

    draftFrom.value = from;
    draftTo.value = to;
    draftChoice.value = preset !== null ? `preset-${preset}` : 'custom';
}

seedDraft();

watch(isOpen, (open) => {
    if (open) {
        seedDraft();
    }
});

// One watcher covering both the moment the popover opens into the custom state and the moment
// the user switches to it while already open — Vue batches the `draftChoice` assignment from
// `seedDraft()` together with the `isOpen` change that caused it, so this fires once per case.
// The width animation is awaited first: positioning a field against a card still mid-transition
// would measure a stale rect and leave the calendar unaligned once the width settles.
watch([isOpen, draftChoice], async ([open, choice]) => {
    if (!open) {
        return;
    }

    await animatePopoverWidth();

    if (choice === 'custom') {
        updateCalendarPositions();
    }
});

const triggerLabel = computed(() => {
    const [from, to] = splitPeriodRangeString(modelValue.value);
    const preset = matchingPresetMonths(from, to);

    return preset !== null ? monthsLabel(preset) : i18n.global.t('global.uiElements.periodRangeDropdown.customOptionLabel');
});

function onFieldChange(field: 'from' | 'to', newValue: string | null) {
    if (!newValue) {
        return;
    }

    if (field === 'from') {
        draftFrom.value = newValue;
    } else {
        draftTo.value = newValue;
    }

    draftChoice.value = 'custom';
}

function presetRangeString(months: number): string {
    const to = new Date();
    const from = subMonths(to, months);

    return joinPeriodRangeString(from.toISOString(), to.toISOString());
}

// Presets are a single click, so they commit and close immediately. Custom needs two valid dates
// first, so picking it only reveals the From/To fields - Apply/Cancel below commit or discard those.
function onChoiceSelected(choice: string) {
    draftChoice.value = choice;

    if (choice === 'custom') {
        return;
    }

    modelValue.value = presetRangeString(Number(choice.replace('preset-', '')));
    isOpen.value = false;
}

function onApply() {
    modelValue.value = joinPeriodRangeString(draftFrom.value, draftTo.value);
    isOpen.value = false;
}

function onCancel() {
    isOpen.value = false;
}
</script>

<style>
/* The field is ~254px but the calendar's auto-width grid (week numbers + day grid + house
   margins) renders ~408px, wider than the field and the popover card itself - it's expected to
   hang off both sides of the slim card as its own floating panel (own bg/border/shadow). Centered
   under the field rather than flush-right so the overflow is even on both sides. */
.period-range-dropdown-field .flatpickr-wrapper {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
}

/* Flatpickr's open animation (fpFadeInDown, a ~300ms fade+slide) is still mid-transition if a
   date is clicked quickly after opening, which briefly reveals whatever sits behind/above the
   calendar through its semi-transparent, offset starting frame - here that's this popover's own
   preset list sitting directly above the Custom fields. Disabled locally rather than in the
   shared FlatpickrWrapper, which 21+ other date pickers elsewhere in the app still rely on. */
.period-range-dropdown-field .flatpickr-calendar.animate.open {
    animation: none;
}
</style>
